import { motion } from 'framer-motion';
import { AiOutlineTikTok } from 'react-icons/ai';
import { FiFacebook, FiInstagram, FiLinkedin } from 'react-icons/fi';

import OptimizedImage from '../OptimizedImage';

const AboutSection = () => {
  return (
    <section id="about" className="section bg-surface dark:bg-panel/95">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-primary dark:text-theme-primary mb-4">
            about us
          </h2>
          <div className="w-24 h-1 bg-secondary mx-auto mb-6"></div>
          <p className="text-lg text-theme-secondary max-w-3xl mx-auto">
          We’re on a mission to create a global ecosystem that transforms the way people deepen their closest relationships.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h3 className="text-2xl font-bold text-primary dark:text-theme-primary mb-4">where our story begins</h3>
            <p className="text-theme-secondary mb-4">
              konnect better was created to make one of life's greatest challenges, building strong relationships, feel simpler and more accessible.
            </p>
            <p className="text-theme-secondary mb-4">
              Through years of conversations and coaching, we learned something powerful: people want to feel closer, more connected, and more understood. But the tools to grow often felt complex or out of reach.
            </p>
            <p className="text-theme-secondary mb-4">
            That’s why we set out to build something: different, practical, and research-backed that can be part of everyday life. We bridge the gap between self-help ideas and real-world practices, offering bite-sized wisdom and supportive tools you can carry with you. 
            </p>
            <p className="text-theme-secondary mb-4">
              Through our books, workbooks, mobile app, and interactive games, we meet you where you are, with guidance that helps you connect more deeply with yourself and with the people who matter most.
            </p>
            
            {/* Social Media Links */}
            <div className="flex space-x-4 mt-2">
              <a href="https://www.instagram.com/konnectbetter/" target="_blank" rel="noopener noreferrer" className="text-theme-secondary hover:text-brand-primary dark:hover:text-brand-secondary transition-colors" aria-label="Instagram">
                <FiInstagram size={20} />
              </a>
              <a href="https://www.tiktok.com/@konnect.better" target="_blank" rel="noopener noreferrer" className="text-theme-secondary hover:text-brand-primary dark:hover:text-brand-secondary transition-colors" aria-label="TikTok">
                <AiOutlineTikTok size={20} />
              </a>
              <a href="https://www.facebook.com/konnectbetter/" target="_blank" rel="noopener noreferrer" className="text-theme-secondary hover:text-brand-primary dark:hover:text-brand-secondary transition-colors" aria-label="Facebook">
                <FiFacebook size={20} />
              </a>
              <a href="https://www.linkedin.com/company/konnectbetter/" target="_blank" rel="noopener noreferrer" className="text-theme-secondary hover:text-brand-primary dark:hover:text-brand-secondary transition-colors" aria-label="LinkedIn">
                <FiLinkedin size={20} />
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            {/* Brand story image */}
            <div className="rounded-lg overflow-hidden shadow-md">
              <OptimizedImage
                src="/images/konnect_better_family.png" 
                alt="Konnect Better Family"
                sizes="(max-width: 768px) 100vw, 50vw"
              />
            </div>
            
            {/* Decorative elements */}
            <div className="absolute -z-10 -bottom-6 -right-6 w-32 h-32 bg-secondary/20 rounded-full blur-xl"></div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-16"
        >
          <div className="bg-card p-6 rounded-lg shadow-sm">
            <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
              </svg>
            </div>
            <h4 className="text-xl font-semibold text-primary dark:text-theme-primary mb-2">research that feels human</h4>
            <p className="text-theme-secondary">
              We create tools rooted in psychology that feel natural to use and rewarding to practice.
            </p>
          </div>

          <div className="bg-card p-6 rounded-lg shadow-sm">
            <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"></path>
              </svg>
            </div>
            <h4 className="text-xl font-semibold text-primary dark:text-theme-primary mb-2">personalization</h4>
            <p className="text-theme-secondary">
              We understand that everyone's journey is unique, so our tools adapt to your specific needs and goals.
            </p>
          </div>

          <div className="bg-card p-6 rounded-lg shadow-sm">
            <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
              </svg>
            </div>
            <h4 className="text-xl font-semibold text-primary dark:text-theme-primary mb-2">community-focused</h4>
            <p className="text-theme-secondary">
              We believe in the power of connection and provide opportunities for shared growth and learning.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
